// Kanban board: one column per on-board stage. Drag a card onto a column to move it (server
// validates; the move dialog opens pre-filled so checklists/required fields can be completed).
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { WfBoardResponse, WfCase } from '@alignova/shared';
import { useLiveEvent } from '../../lib/useLive.ts';
import { useWfConfig, wfApi } from './workflowApi.ts';
import { CaseActionModal, type ActionTarget, type WfAction } from './WfActions.tsx';
import { CaseCard, Empty, inputCls } from './wfUi.tsx';
import { Sheet } from '../../ui/mobile.tsx';
import { Menu } from '../../ui/Menu.tsx';

const GROUP_FILTERS = [
  { key: '', label: 'All lanes' },
  { key: 'intake', label: 'Intake' },
  { key: 'records', label: 'Records' },
  { key: 'design', label: 'Design' },
  { key: 'review', label: 'Review' },
  { key: 'approval', label: 'Approval' },
  { key: 'billing', label: 'Invoice' },
  { key: 'production', label: 'Production' },
  { key: 'finishing', label: 'Finishing' },
  { key: 'dispatch', label: 'Dispatch' },
  { key: 'special', label: 'Special queues' },
];

export function WfBoardPage() {
  const nav = useNavigate();
  const cfg = useWfConfig();
  const [sp, setSp] = useSearchParams();
  const [board, setBoard] = useState<WfBoardResponse | null>(null);
  const [target, setTarget] = useState<ActionTarget | null>(null);
  // The workflow toolbar owns the search field and writes ?search= — this reads it.
  const search = sp.get('search') ?? '';
  const group = sp.get('group') ?? '';
  const assignedTo = sp.get('assignedTo') ?? '';
  const caseType = sp.get('caseType') ?? '';
  const priority = sp.get('priority') ?? '';
  const [dragOver, setDragOver] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const load = useCallback(() => {
    wfApi
      .board({ group: group || undefined, assignedTo: assignedTo || undefined, caseType: caseType ? [caseType] : undefined, priority: priority ? [priority as never] : undefined, search: search || undefined, perStage: 100 })
      .then((b) => {
        setBoard(b);
        setErr(null);
      })
      .catch((e) => setErr(e.message));
  }, [group, assignedTo, caseType, priority, search]);
  useEffect(load, [load]);
  useLiveEvent('workflow.changed', load);

  const setParam = (k: string, v: string) => {
    const n = new URLSearchParams(sp);
    if (v) n.set(k, v);
    else n.delete(k);
    setSp(n, { replace: true });
  };

  const onAction = (action: string, c: WfCase) => {
    if (action === 'open') return nav(`/workflow/cases/${c.id}`);
    setTarget({ action: action as WfAction, c });
  };
  const onDrop = (stageKey: string, e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(null);
    const raw = e.dataTransfer.getData('application/x-wf-case');
    if (!raw) return;
    const c = JSON.parse(raw) as WfCase;
    if (c.stageKey === stageKey) return;
    if (stageKey === 'on_hold') return setTarget({ action: 'hold', c });
    if (stageKey === 'case_cancelled') return setTarget({ action: 'cancel', c });
    if (c.stageKey === 'on_hold') return setTarget({ action: 'resume', c, toStage: stageKey });
    setTarget({ action: 'move', c, toStage: stageKey });
  };
  const columns = useMemo(() => board?.columns ?? [], [board]);
  const total = columns.reduce((a, c) => a + c.count, 0);

  // Search plus the three dropdowns: inline on a desk, inside a sheet on a phone, where a wrapping
  // row of five controls used to push the board itself below the fold.
  const controls = (
    <>
      <select className={`${inputCls} lg:!w-40 lg:shrink-0`} aria-label="Assigned to" value={assignedTo} onChange={(e) => setParam('assignedTo', e.target.value)}>
        <option value="">Everyone</option>
        <option value="me">My cases</option>
        <option value="unassigned">Unassigned</option>
        {cfg.staff.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
      </select>
      <select className={`${inputCls} lg:!w-40 lg:shrink-0`} aria-label="Case type" value={caseType} onChange={(e) => setParam('caseType', e.target.value)}>
        <option value="">All case types</option>
        {cfg.optionsOf('case_type').map((o) => (
          <option key={o.key} value={o.key}>
            {o.label}
          </option>
        ))}
      </select>
      <select className={`${inputCls} lg:!w-36 lg:shrink-0`} aria-label="Priority" value={priority} onChange={(e) => setParam('priority', e.target.value)}>
        <option value="">Any priority</option>
        <option value="urgent">Urgent</option>
        <option value="emergency">Emergency</option>
      </select>
    </>
  );
  const activeFilters = [search, assignedTo, caseType, priority].filter(Boolean).length;

  return (
    // Fills whatever height the workflow layout has left, rather than subtracting a guess at the
    // chrome above from the viewport — a guess that goes stale the moment the toolbar changes.
    <div className="flex h-full min-h-[26rem] flex-col">
      {/* One row: which lanes, then how they are filtered. The three dropdowns used to occupy a
          row of their own directly under a row of pills, directly under the toolbar - three bands
          of chrome before any case was visible. */}
      <div className="mb-3 flex items-center gap-2">
        <div className="no-scrollbar snap-strip -mx-3 flex min-w-0 flex-1 items-center gap-1 overflow-x-auto px-3 lg:mx-0 lg:px-0">
          {GROUP_FILTERS.map((g) => (
            <button
              key={g.key}
              onClick={() => setParam('group', g.key)}
              className={`tap shrink-0 rounded-md px-3 py-1.5 text-[13px] font-medium tracking-tight transition ${
                group === g.key ? 'bg-primary text-primary-fg' : 'text-muted hover:bg-surface-2 hover:text-ink'
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>

        <span className="tabnum hidden shrink-0 text-xs text-muted lg:inline">{total} cases</span>

        {/* Phone: a sheet. Desk: the same controls in a dropdown, so they cost no vertical space. */}
        <button
          type="button"
          onClick={() => setFiltersOpen(true)}
          className="tap flex h-9 shrink-0 items-center gap-1.5 rounded-md border border-line px-3 text-xs font-medium text-ink lg:hidden"
        >
          Filter
          {activeFilters > 0 && (
            <span className="tabnum grid h-4 w-4 place-items-center rounded-full bg-accent text-[10px] font-bold text-accent-fg">
              {activeFilters}
            </span>
          )}
        </button>
        <span className="hidden shrink-0 lg:block">
          <Menu label="Filter the board" trigger={<FilterGlyph active={activeFilters > 0} count={activeFilters} />}>
            {() => <div className="w-64 space-y-2 p-2">{controls}</div>}
          </Menu>
        </span>
      </div>
      <p className="tabnum mb-2 text-xs text-muted lg:hidden">{total} cases on the board</p>
      {err && <div className="mb-2 text-sm text-status-urgent-fg">{err}</div>}
      <div className="no-scrollbar snap-strip -mx-3 flex min-h-0 flex-1 gap-3 overflow-x-auto px-3 pb-3 lg:mx-0 lg:px-0">
        {columns.map((col) => (
          <div
            key={col.stage.key}
            onDragOver={(e) => {
              e.preventDefault();
              if (dragOver !== col.stage.key) setDragOver(col.stage.key);
            }}
            onDragLeave={() => setDragOver((d) => (d === col.stage.key ? null : d))}
            onDrop={(e) => onDrop(col.stage.key, e)}
            className={`flex w-[86vw] max-w-sm flex-shrink-0 flex-col rounded-lg border bg-surface-2/60 sm:w-72 ${dragOver === col.stage.key ? 'border-accent ring-2 ring-accent/40' : 'border-line'}`}
          >
            <div className="sticky top-0 flex items-center justify-between rounded-t-lg border-b border-line bg-surface px-3 py-2" style={{ borderTop: `3px solid ${col.stage.color ?? '#64748B'}` }}>
              <button className="truncate text-left text-sm font-semibold text-ink hover:underline" onClick={() => nav(`/workflow/cases?stage=${col.stage.key}`)} title="Open as list">
                {col.stage.name}
              </button>
              <span className="flex items-center gap-1">
                {col.stage.slaHours != null && col.cases.some((c) => c.slaBreached) && <span className="tabnum rounded bg-status-urgent-bg px-1 text-[10px] font-bold text-status-urgent-fg" title="over SLA">{col.cases.filter((c) => c.slaBreached).length} SLA</span>}
                <span className="rounded-full bg-surface-2 px-2 font-mono text-xs font-semibold">{col.count}</span>
              </span>
            </div>
            <div className="flex-1 space-y-2 overflow-y-auto p-2">
              {col.cases.map((c) => (
                <CaseCard key={c.id} c={c} onAction={onAction} draggable onDragStart={(e, cc) => e.dataTransfer.setData('application/x-wf-case', JSON.stringify(cc))} />
              ))}
              {!col.cases.length && <Empty>Empty</Empty>}
              {col.count > col.cases.length && (
                <button className="w-full rounded border border-dashed border-line py-1 text-xs text-muted hover:bg-surface" onClick={() => nav(`/workflow/cases?stage=${col.stage.key}`)}>
                  +{col.count - col.cases.length} more - open list
                </button>
              )}
            </div>
          </div>
        ))}
        {!columns.length && !err && <div className="skeleton h-full w-[86vw] max-w-sm shrink-0 sm:w-72" role="status" aria-label="Loading board" />}
      </div>
      <Sheet open={filtersOpen} onClose={() => setFiltersOpen(false)} title="Filter the board">
        <div className="space-y-3 pt-1">{controls}</div>
      </Sheet>
      <CaseActionModal target={target} onClose={() => setTarget(null)} onDone={() => { setTarget(null); load(); }} />
    </div>
  );
}

/** Funnel with a count, so a filtered board never looks like an empty one. */
function FilterGlyph({ active, count }: { active: boolean; count: number }) {
  return (
    <span className="relative grid place-items-center">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 5.5h16l-6.2 7.3v5.4l-3.6 1.8v-7.2Z" />
      </svg>
      {active && (
        <span className="tabnum absolute -right-2 -top-1.5 grid h-4 min-w-[1rem] place-items-center rounded-full bg-accent px-1 text-[10px] font-bold text-accent-fg">
          {count}
        </span>
      )}
    </span>
  );
}
